import React, { useEffect, useRef, useState } from 'react';

type Variant = 'up' | 'fade' | 'scale' | 'left' | 'right';

interface RevealProps {
  children: React.ReactNode;
  /** Dirección/estilo de la entrada. */
  variant?: Variant;
  /** Retraso en ms, para escalonar elementos de una misma fila. */
  delay?: number;
  /** Etiqueta a renderizar (por defecto div). */
  as?: 'div' | 'li' | 'section' | 'article';
  className?: string;
}

/* ──────────────────────────────────────────────────────────────────
   Registro compartido.

   Se mide la posición del elemento en lugar de usar IntersectionObserver
   a propósito: IO solo dispara cuando la página está componiendo, así que
   en una pestaña en segundo plano —o en un navegador headless— el
   contenido se quedaba invisible de forma permanente. Como aquí el
   contenido se oculta con CSS, ese fallo sería el peor posible.

   Un solo listener pasivo para todo el sitio, limitado por
   requestAnimationFrame, y cada elemento se da de baja al aparecer.
   ────────────────────────────────────────────────────────────────── */
type Entry = { el: HTMLElement; show: () => void };
const pending = new Set<Entry>();
let listening = false;
let frame = 0;

/** Margen: se revela un poco antes de que el elemento toque el borde inferior. */
const MARGIN = 60;

function flush() {
  frame = 0;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  for (const entry of [...pending]) {
    const r = entry.el.getBoundingClientRect();
    // Visible o ya rebasado hacia arriba: un salto al final no deja nada oculto.
    if (r.top < vh - MARGIN) {
      entry.show();
      pending.delete(entry);
    }
  }
  if (pending.size === 0) stop();
}

function schedule() {
  /* rAF agrupa varios eventos de scroll en un solo cálculo. Si por lo que sea
     no llega a ejecutarse, un temporizador corto hace de red de seguridad para
     que nada quede oculto. */
  if (frame) return;
  frame = requestAnimationFrame(flush);
  window.setTimeout(() => {
    if (frame) {
      cancelAnimationFrame(frame);
      flush();
    }
  }, 120);
}

function start() {
  if (listening) return;
  listening = true;
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
}

function stop() {
  if (!listening) return;
  listening = false;
  window.removeEventListener('scroll', schedule);
  window.removeEventListener('resize', schedule);
}

/* ── Red de seguridad ──────────────────────────────────────────────
   Si el entorno no anima (pestaña en segundo plano, navegador headless,
   ahorro de energía), no llegan ni scroll ni requestAnimationFrame, y
   todo lo que está fuera de pantalla se quedaría oculto para siempre.

   Se comprueba una sola vez si rAF llega a ejecutarse. Si no, se muestra
   todo: es preferible perder la animación a perder el contenido. */
/** null = sin comprobar todavía · true = el entorno anima · false = no anima. */
let envAnimates: boolean | null = null;
let probing = false;

function revealAllPending() {
  for (const entry of [...pending]) {
    entry.show();
    pending.delete(entry);
  }
  stop();
}

function probeEnvironment() {
  if (envAnimates !== null || probing || typeof window === 'undefined') return;
  probing = true;

  let fired = false;
  requestAnimationFrame(() => {
    fired = true;
  });

  window.setTimeout(() => {
    probing = false;
    envAnimates = fired;
    /* El resultado se recuerda para el resto de la sesión: en una SPA se
       montan elementos nuevos en cada navegación y volver a sondear cada vez
       dejaría a esos elementos sin red durante 1,5 s. */
    if (!fired) revealAllPending();
  }, 1500);
}

/** Comprueba un elemento concreto, sin pasar por rAF. */
function isInView(el: HTMLElement) {
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const r = el.getBoundingClientRect();
  return r.top < vh - MARGIN;
}

function register(entry: Entry) {
  /* La primera comprobación es SÍNCRONA a propósito. requestAnimationFrame no
     se ejecuta mientras la página no está componiendo (pestaña en segundo
     plano, navegador headless), y delegar en él dejaba invisible todo lo que
     ya estaba en pantalla al cargar. */
  if (isInView(entry.el)) {
    entry.show();
    return () => {};
  }

  // Si ya se comprobó que este entorno no anima, no tiene sentido esperar.
  if (envAnimates === false) {
    entry.show();
    return () => {};
  }

  pending.add(entry);
  start();
  probeEnvironment();
  return () => {
    pending.delete(entry);
    if (pending.size === 0) stop();
  };
}

/**
 * Entrada al hacer scroll. Solo se animan `opacity` y `transform`, que el
 * compositor resuelve sin recalcular layout: el elemento ocupa su espacio
 * desde el primer pintado, así que no produce saltos de contenido.
 *
 * Si el usuario pidió menos movimiento, el contenido aparece directamente.
 */
const Reveal: React.FC<RevealProps> = ({
  children,
  variant = 'up',
  delay = 0,
  as: Tag = 'div',
  className = '',
}) => {
  const ref = useRef<HTMLElement>(null);
  /* `armed` = oculto a la espera del scroll. Empieza en false, igual que el
     HTML prerenderizado: el contenido se publica VISIBLE y solo se oculta,
     ya con JavaScript funcionando, lo que está fuera de pantalla. Antes se
     publicaba con opacity:0 y en un celular lento la página se veía vacía
     hasta que el JavaScript terminaba de cargar. */
  const [armed, setArmed] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches || isInView(el)) {
      return;
    }
    /* Dentro de un rAF a propósito: si el entorno no anima (pestaña en
       segundo plano) el rAF no corre y el contenido se queda visible. */
    let unregister = () => {};
    const raf = requestAnimationFrame(() => {
      if (isInView(el)) return;
      setArmed(true);
      unregister = register({ el, show: () => setShown(true) });
    });
    return () => {
      cancelAnimationFrame(raf);
      unregister();
    };
  }, []);

  return (
    <Tag
      ref={ref as React.Ref<never>}
      className={`reveal reveal--${variant} ${armed ? 'is-armed' : ''} ${shown ? 'is-shown' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
